import { motion } from "framer-motion";
import { springPresets, hoverLift } from "@/lib/motion";
import { Service, Facility, Equipment, Testimonial } from "@/lib/index";
import { Film, Camera, Clapperboard, Video, Sparkles, Award, Building2, Lightbulb, Globe, Tv, Megaphone } from "lucide-react";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  film: Film,
  camera: Camera,
  clapperboard: Clapperboard,
  video: Video,
  sparkles: Sparkles,
  award: Award,
  building2: Building2,
  lightbulb: Lightbulb,
  globe: Globe,
  tv: Tv,
  megaphone: Megaphone,
};

interface ServiceCardProps {
  service: Service;
}

export function ServiceCard({ service }: ServiceCardProps) {
  const IconComponent = iconMap[service.icon.toLowerCase()] || Film;

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="group relative bg-card rounded-2xl p-8 border border-border overflow-hidden"
      style={{
        boxShadow: "0 8px 30px -6px color-mix(in srgb, var(--primary) 15%, transparent)",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative z-10">
        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors duration-300">
          <IconComponent className="w-7 h-7 text-primary" />
        </div>
        
        <h3 className="text-2xl font-semibold mb-3 text-foreground">
          {service.title}
        </h3>
        
        <p className="text-muted-foreground mb-6 leading-relaxed">
          {service.description}
        </p>
        
        <ul className="space-y-2">
          {service.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

interface FacilityCardProps {
  facility: Facility;
}

export function FacilityCard({ facility }: FacilityCardProps) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="group relative bg-card rounded-2xl overflow-hidden border border-border"
      style={{
        boxShadow: "0 8px 30px -6px color-mix(in srgb, var(--primary) 15%, transparent)",
      }}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={facility.image}
          alt={facility.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-90" />
        
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="text-2xl font-semibold text-foreground mb-2">
            {facility.name}
          </h3>
          <p className="text-muted-foreground text-sm">
            {facility.description}
          </p>
        </div>
      </div>
      
      <div className="p-6">
        <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">
          Specifications
        </h4>
        <ul className="space-y-2">
          {facility.specifications.map((spec, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
              <span>{spec}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

interface EquipmentCardProps {
  equipment: Equipment;
}

export function EquipmentCard({ equipment }: EquipmentCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.gentle}
      className="bg-card rounded-xl p-6 border border-border"
      style={{
        boxShadow: "0 4px 12px color-mix(in srgb, var(--primary) 10%, transparent)",
      }}
    >
      {equipment.image && (
        <div className="relative aspect-video rounded-lg overflow-hidden mb-4">
          <img
            src={equipment.image}
            alt={equipment.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs font-medium text-accent uppercase tracking-wider mb-1">
            {equipment.category}
          </p>
          <h4 className="text-lg font-semibold text-foreground">
            {equipment.name}
          </h4>
        </div>
      </div>
      
      <ul className="space-y-1.5">
        {equipment.specifications.map((spec, index) => (
          <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground font-mono">
            <span className="text-primary mt-0.5">•</span>
            <span>{spec}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

interface TestimonialCardProps {
  testimonial: Testimonial;
}

export function TestimonialCard({ testimonial }: TestimonialCardProps) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="bg-card rounded-2xl p-8 border border-border relative"
      style={{
        boxShadow: "0 8px 30px -6px color-mix(in srgb, var(--primary) 15%, transparent)",
      }}
    >
      <div className="absolute top-6 right-6 text-6xl text-primary/10 font-serif leading-none">
        &ldquo;
      </div>
      
      <div className="relative z-10">
        <p className="text-lg text-foreground mb-6 leading-relaxed italic">
          {testimonial.quote}
        </p>
        
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-lg font-semibold text-primary">
              {testimonial.name.charAt(0)}
            </span>
          </div>
          
          <div>
            <p className="font-semibold text-foreground">
              {testimonial.name}
            </p>
            <p className="text-sm text-muted-foreground">
              {testimonial.role}, {testimonial.company}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}